import { useState, type FormEvent } from "react";
import { useParams } from "react-router-dom";
import { apiFetch } from "../api/client";
import type {
  TestWithQuestions,
  Question,
  QuestionImageRef,
} from "../api/types";
import { useFetch } from "../hooks/useFetch";
import { ErrorBanner } from "../components/ErrorBanner";
import { QuestionImages } from "../components/QuestionImages";
import { QuestionImageManager } from "../components/QuestionImageManager";
import { MarkdownImportSection } from "../components/MarkdownImportSection";
import {
  EmptyState,
  LoadingState,
  PageHeader,
  StatusBadge,
} from "../components/ui";

interface DetailResponse {
  test: TestWithQuestions;
}

export function TestEditorPage() {
  const params = useParams<{ testId: string }>();
  const testId = params.testId!;
  const { data, loading, error, refetch } = useFetch<DetailResponse>(
    `/admin/tests/${testId}`,
  );
  const [actionError, setActionError] = useState<Error | null>(null);

  function showError(err: unknown) {
    setActionError(err instanceof Error ? err : new Error(String(err)));
  }

  async function updateTest(patch: Partial<TestWithQuestions>) {
    setActionError(null);
    try {
      await apiFetch(`/admin/tests/${testId}`, {
        method: "PUT",
        body: JSON.stringify(patch),
      });
      refetch();
    } catch (e) {
      showError(e);
    }
  }

  async function addQuestion(body: unknown) {
    try {
      await apiFetch(`/admin/tests/${testId}/questions`, {
        method: "POST",
        body: JSON.stringify(body),
      });
      refetch();
    } catch (e) {
      showError(e);
    }
  }

  async function deleteQuestion(qid: string) {
    if (!confirm("Delete question?")) return;
    try {
      await apiFetch(`/admin/questions/${qid}`, { method: "DELETE" });
      refetch();
    } catch (e) {
      showError(e);
    }
  }

  async function updateQuestion(qid: string, body: unknown) {
    try {
      await apiFetch(`/admin/questions/${qid}`, {
        method: "PUT",
        body: JSON.stringify(body),
      });
      refetch();
    } catch (e) {
      showError(e);
    }
  }

  if (loading) return <LoadingState />;
  if (error) return <ErrorBanner error={error} />;
  if (!data) return <p>Not found.</p>;
  const test = data.test;

  return (
    <section>
      <PageHeader
        title={test.title}
        subtitle={
          <span>
            <StatusBadge status={test.status} />
            {test.duration_minutes
              ? ` · ${test.duration_minutes} min duration`
              : ""}
          </span>
        }
      />
      <ErrorBanner error={actionError} />

      <TestDetailsForm test={test} onSubmit={updateTest} />

      <h2>Questions</h2>
      {test.questions.length === 0 ? (
        <EmptyState
          title="No questions yet"
          hint="Add a question with the form below, or bulk-import from Markdown."
        />
      ) : (
        <ol>
          {test.questions.map((q) => (
            <QuestionView
              key={q.id}
              q={q}
              onDelete={() => void deleteQuestion(q.id)}
              onSave={(body) => void updateQuestion(q.id, body)}
            />
          ))}
        </ol>
      )}

      <NewQuestionForm onSubmit={addQuestion} />

      <MarkdownImportSection testId={testId} onImported={refetch} />
    </section>
  );
}

function TestDetailsForm({
  test,
  onSubmit,
}: {
  test: TestWithQuestions;
  onSubmit: (patch: Record<string, unknown>) => void;
}) {
  const [title, setTitle] = useState(test.title);
  const [description, setDescription] = useState(test.description ?? "");
  const [status, setStatus] = useState(test.status);
  const [startsAt, setStartsAt] = useState(test.starts_at ?? "");
  const [endsAt, setEndsAt] = useState(test.ends_at ?? "");
  const [duration, setDuration] = useState<string>(
    test.duration_minutes !== null ? String(test.duration_minutes) : "",
  );
  function submit(e: FormEvent) {
    e.preventDefault();
    onSubmit({
      title,
      description: description || null,
      status,
      starts_at: startsAt || null,
      ends_at: endsAt || null,
      duration_minutes: duration === "" ? null : Number(duration),
    });
  }
  return (
    <form className="inline-form admin-form" onSubmit={submit}>
      <fieldset>
        <legend>Test details</legend>
        <label className="field-block">
          Title
          <input
            className="field-wide"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
        </label>
        <label className="field-block">
          Description
          <textarea
            className="admin-textarea"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Optional — visible to students on the test page."
          />
        </label>
        <label className="field-block">
          Status
          <select
            value={status}
            onChange={(e) =>
              setStatus(e.target.value as typeof status)
            }
          >
            <option value="draft">draft</option>
            <option value="active">active</option>
            <option value="closed">closed</option>
            <option value="archived">archived</option>
          </select>
        </label>
        <div className="datetime-row">
          <label className="field-block">
            Starts at (ISO)
            <input
              className="field-wide"
              value={startsAt}
              onChange={(e) => setStartsAt(e.target.value)}
              placeholder="2026-06-01T09:00:00Z"
            />
          </label>
          <label className="field-block">
            Ends at (ISO)
            <input
              className="field-wide"
              value={endsAt}
              onChange={(e) => setEndsAt(e.target.value)}
              placeholder="2026-06-01T10:00:00Z"
            />
          </label>
          <label className="field-block">
            Duration (minutes)
            <input
              className="field-wide"
              type="number"
              min={1}
              max={24 * 60}
              value={duration}
              onChange={(e) => setDuration(e.target.value)}
              placeholder="e.g. 60"
            />
          </label>
        </div>
        <button type="submit">Save</button>
      </fieldset>
    </form>
  );
}

function QuestionView({
  q,
  onDelete,
  onSave,
}: {
  q: Question;
  onDelete: () => void;
  onSave: (body: Record<string, unknown>) => void;
}) {
  const [editing, setEditing] = useState(false);

  if (editing) {
    return (
      <li>
        <EditQuestionForm
          q={q}
          onCancel={() => setEditing(false)}
          onSave={(body) => {
            onSave(body);
            setEditing(false);
          }}
        />
      </li>
    );
  }

  return (
    <li>
      <strong>{q.prompt}</strong>{" "}
      <small>
        ({q.type}, {q.points} pts)
      </small>
      <button onClick={() => setEditing(true)}>Edit</button>
      <button onClick={onDelete}>Delete</button>
      <QuestionImages images={q.images} kind="admin" />
      {q.type === "mcq" && q.answer_options.length > 0 && (
        <ul>
          {q.answer_options.map((o) => (
            <li key={o.id}>
              {o.is_correct ? "✓ " : ""}
              {o.text}
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}

function EditQuestionForm({
  q,
  onCancel,
  onSave,
}: {
  q: Question;
  onCancel: () => void;
  onSave: (body: Record<string, unknown>) => void;
}) {
  const [prompt, setPrompt] = useState(q.prompt);
  const [points, setPoints] = useState(q.points);
  const [position, setPosition] = useState(q.position);
  const [images, setImages] = useState<QuestionImageRef[]>(q.images ?? []);
  // MCQ-only: editable copies of the existing answer options. When the form
  // is submitted these replace the question's options atomically on the
  // backend (PUT /admin/questions/:id with answer_options is all-or-nothing).
  const [options, setOptions] = useState<
    Array<{ text: string; is_correct: boolean }>
  >(
    q.type === "mcq"
      ? q.answer_options.map((o) => ({
          text: o.text,
          is_correct: !!o.is_correct,
        }))
      : [],
  );

  function submit(e: FormEvent) {
    e.preventDefault();
    if (!prompt.trim()) return;
    const body: Record<string, unknown> = {
      prompt: prompt.trim(),
      points,
      position,
    };
    if (q.type === "mcq") {
      body.answer_options = options
        .filter((o) => o.text.trim())
        .map((o) => ({ text: o.text.trim(), is_correct: o.is_correct }));
    }
    onSave(body);
  }

  return (
    <form className="inline-form admin-form" onSubmit={submit}>
      <fieldset>
        <legend>Edit question</legend>
        <label className="field-block">
          Prompt
          <textarea
            className="admin-textarea"
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
          />
        </label>
        <div className="datetime-row">
          <label className="field-block">
            Points
            <input
              className="field-wide"
              type="number"
              min={0}
              value={points}
              onChange={(e) => setPoints(Number(e.target.value))}
            />
          </label>
          <label className="field-block">
            Position
            <input
              className="field-wide"
              type="number"
              min={0}
              value={position}
              onChange={(e) => setPosition(Number(e.target.value))}
            />
          </label>
        </div>
        {q.type === "mcq" && (
          <div>
            <p>Answer options</p>
            {options.map((o, i) => (
              <div key={i} style={{ marginBottom: "0.4rem" }}>
                <input
                  className="field-wide"
                  placeholder={`option ${i + 1}`}
                  value={o.text}
                  onChange={(e) =>
                    setOptions((arr) =>
                      arr.map((x, j) =>
                        j === i ? { ...x, text: e.target.value } : x,
                      ),
                    )
                  }
                />
                <label>
                  <input
                    type="checkbox"
                    checked={o.is_correct}
                    onChange={(e) =>
                      setOptions((arr) =>
                        arr.map((x, j) =>
                          j === i
                            ? { ...x, is_correct: e.target.checked }
                            : x,
                        ),
                      )
                    }
                  />{" "}
                  correct
                </label>{" "}
                <button
                  type="button"
                  onClick={() =>
                    setOptions((arr) => arr.filter((_, j) => j !== i))
                  }
                >
                  Remove
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={() =>
                setOptions((arr) => [
                  ...arr,
                  { text: "", is_correct: false },
                ])
              }
            >
              Add option
            </button>
          </div>
        )}
        {/* Image upload is a side-channel — it POSTs directly to the
            backend and isn't part of the question PUT body. That's fine
            because each upload has its own id; the parent just needs the
            updated metadata list to render. */}
        <QuestionImageManager
          questionId={q.id}
          initialImages={images}
          onChange={setImages}
        />
        <div className="actions">
          <button type="submit">Save changes</button>{" "}
          <button type="button" onClick={onCancel}>
            Cancel
          </button>
        </div>
      </fieldset>
    </form>
  );
}

function NewQuestionForm({ onSubmit }: { onSubmit: (body: unknown) => void }) {
  const [type, setType] = useState<"mcq" | "free_text">("mcq");
  const [prompt, setPrompt] = useState("");
  const [points, setPoints] = useState(1);
  const [options, setOptions] = useState<
    Array<{ text: string; is_correct: boolean }>
  >([
    { text: "", is_correct: false },
    { text: "", is_correct: false },
  ]);

  function submit(e: FormEvent) {
    e.preventDefault();
    if (!prompt.trim()) return;
    if (type === "mcq") {
      onSubmit({
        type: "mcq",
        prompt: prompt.trim(),
        points,
        answer_options: options
          .filter((o) => o.text.trim())
          .map((o) => ({ text: o.text.trim(), is_correct: o.is_correct })),
      });
    } else {
      onSubmit({ type: "free_text", prompt: prompt.trim(), points });
    }
    setPrompt("");
    setPoints(1);
    setOptions([
      { text: "", is_correct: false },
      { text: "", is_correct: false },
    ]);
  }

  return (
    <form className="inline-form admin-form" onSubmit={submit}>
      <fieldset>
        <legend>Add question</legend>
        <label className="field-block">
          Type
          <select
            value={type}
            onChange={(e) => setType(e.target.value as "mcq" | "free_text")}
          >
            <option value="mcq">MCQ</option>
            <option value="free_text">Free text</option>
          </select>
        </label>
        <label className="field-block">
          Prompt
          <textarea
            className="admin-textarea"
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            placeholder="Question text — students see this verbatim."
          />
        </label>
        <label className="field-block">
          Points
          <input
            type="number"
            min={0}
            value={points}
            onChange={(e) => setPoints(Number(e.target.value))}
          />
        </label>
        {type === "mcq" && (
          <div>
            <p>Answer options</p>
            {options.map((o, i) => (
              <div key={i}>
                <input
                  placeholder={`option ${i + 1}`}
                  value={o.text}
                  onChange={(e) =>
                    setOptions((arr) =>
                      arr.map((x, j) =>
                        j === i ? { ...x, text: e.target.value } : x,
                      ),
                    )
                  }
                />
                <label>
                  <input
                    type="checkbox"
                    checked={o.is_correct}
                    onChange={(e) =>
                      setOptions((arr) =>
                        arr.map((x, j) =>
                          j === i
                            ? { ...x, is_correct: e.target.checked }
                            : x,
                        ),
                      )
                    }
                  />
                  correct
                </label>
              </div>
            ))}
            <button
              type="button"
              onClick={() =>
                setOptions((arr) => [...arr, { text: "", is_correct: false }])
              }
            >
              Add option
            </button>
          </div>
        )}
        <button type="submit">Add question</button>
      </fieldset>
    </form>
  );
}
